import React from 'react';
import type { PosturePictureSnapshot } from '@/lib/posture-analysis/posture-pic-evaluation/types';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { usePostureMode } from '@/components/posture-analysis/lib/PostureModeContext';
import {
    getLaymanGroundingSide,
    getLaymanKeyMetric,
    getLaymanLateralBadge,
    getLaymanPatternChipLabel,
    getLaymanPatternLabel,
    getLaymanPhase1Goal,
    getLaymanReasoningLine,
    getLaymanSignal,
    getLaymanStrategyLabel,
    LAYMAN_CLASSIFICATION_UI,
} from '@/components/posture-analysis/self-assessment/classification-display-labels';

const clampPct = (n: number | undefined | null): number => {
    const x = typeof n === 'number' && isFinite(n) ? n : 0;
    return Math.max(0, Math.min(100, Math.round(x)));
};

const pctColor = (pct: number): string => {
    if (pct >= 85) return 'bg-status-clear text-accent-fg';
    if (pct >= 70) return 'bg-accent text-accent-fg';
    if (pct >= 60) return 'bg-status-warn text-accent-fg';
    return 'bg-status-deviation text-accent-fg';
};

export const patternChip = (pattern: string): { label: string; className: string } => {
    const p = (pattern || '').toLowerCase();
    if (p.includes('sway')) return {
        label: 'Swayback',
        className: 'bg-accent/10 text-accent border-accent/30',
    };
    if (p.includes('posterior lower compression') || p.includes('plc')) return {
        label: 'APT with PLC',
        className: 'bg-status-info-surface text-status-info border-status-info/40',
    };
    if (p.includes('anterior')) return {
        label: 'Anterior Pelvic Tilt',
        className: 'bg-status-warn-surface text-status-warn border-status-warn/45',
    };
    if (p.includes('stack')) return {
        label: 'Stacked',
        className: 'bg-status-clear-surface text-status-clear border-status-clear/40',
    };
    return { label: pattern, className: 'bg-bg text-muted border-line' };
};

const ConfidenceBar: React.FC<{
    value?: number;
    label: string;
    subLabel?: string;
    showConfidence?: boolean;
}> = ({ value, label, subLabel, showConfidence = true }) => {
    const pct = clampPct(value);
    return (
        <div className="space-y-1.5">
            <div className="flex items-baseline justify-between gap-2">
                <div className="text-sm font-bold text-fg font-['Poppins',sans-serif]">
                    {label}{subLabel ? <span className="text-xs font-normal text-muted font-['Roboto',sans-serif]"> — {subLabel}</span> : null}
                </div>
                {showConfidence ? <div className="text-sm font-bold tabular-nums text-fg">{pct}%</div> : null}
            </div>
            {showConfidence ? (
                <div className="h-2.5 w-full rounded-full bg-bg overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-500 ${pctColor(pct)}`} style={{ width: `${pct}%` }} />
                </div>
            ) : null}
        </div>
    );
};

/**
 * Parses the strategy meta line emitted as the first item in reasoning.global:
 * "[strategy name] Orientation — classification confidence X% (Y/Z pts)"
 */
function parseStrategyMetaLine(line: string): {
    pts: string;
    maxPts: string;
    confidencePct: string;
    orientation: string;
    strategyName: string;
} | null {
    const ptsMatch = line.match(/\((\d+)\/(\d+)\s*pts\)/);
    const confMatch = line.match(/classification confidence\s+(\d+)%/);
    const stratMatch = line.match(/^\[([^\]]+)\]/);
    const oriMatch = line.match(/\]\s+([^—]+?)\s+—/);
    if (!ptsMatch || !confMatch) return null;
    return {
        pts: ptsMatch[1],
        maxPts: ptsMatch[2],
        confidencePct: confMatch[1],
        orientation: oriMatch ? oriMatch[1].trim() : '',
        strategyName: stratMatch ? stratMatch[1].trim() : '',
    };
}

const GlobalPatternEvidenceList: React.FC<{ lines: string[]; laymanMode: boolean }> = ({ lines, laymanMode }) => {
    if (lines.length === 0) return null;

    const meta = !laymanMode ? parseStrategyMetaLine(lines[0]) : null;

    if (!meta) {
        // Layman mode or unparseable — render everything as bullets
        return (
            <ul className="space-y-1.5">
                {lines.map((r, i) => (
                    <li key={i} className="text-sm flex items-start gap-2 text-muted">
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-accent flex-shrink-0" />
                        <span>{laymanMode ? getLaymanReasoningLine(r) : r}</span>
                    </li>
                ))}
            </ul>
        );
    }

    const evidenceBullets = lines.slice(1);
    const ptsInt = parseInt(meta.pts, 10);
    const maxPtsInt = parseInt(meta.maxPts, 10);

    return (
        <div className="space-y-2.5">
            {/* Strategy summary header */}
            <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 rounded-full bg-accent/10 border border-accent/30 px-2.5 py-0.5 text-xs font-bold text-accent">
                    {meta.pts}/{meta.maxPts} pts
                </span>
                <span className="text-xs text-muted">
                    {meta.confidencePct}% from image
                </span>
                {meta.strategyName ? (
                    <span className="text-[10px] text-muted italic truncate max-w-[160px]" title={meta.strategyName}>
                        {meta.strategyName.length > 40 ? meta.strategyName.slice(0, 40) + '…' : meta.strategyName}
                    </span>
                ) : null}
            </div>

            {evidenceBullets.length > 0 ? (
                <div>
                    <div className="text-[10px] font-semibold text-muted uppercase tracking-wider mb-1.5">
                        {ptsInt === 0
                            ? 'No scoring signals detected'
                            : `Scoring signals (${ptsInt} of ${maxPtsInt} points)`}
                    </div>
                    <ul className="space-y-1.5">
                        {evidenceBullets.map((r, i) => {
                            const ptMatch = r.match(/^\[(\+\d+)\]\s*/);
                            const ptLabel = ptMatch ? ptMatch[1] : null;
                            const text = ptLabel ? r.slice(ptMatch![0].length) : r;
                            return (
                                <li key={i} className="text-sm flex items-start gap-2 text-muted">
                                    {ptLabel ? (
                                        <span className="mt-0.5 inline-flex items-center rounded-full bg-accent/10 border border-accent/30 px-1.5 py-0 text-[10px] font-bold text-accent flex-shrink-0 leading-5">
                                            {ptLabel}
                                        </span>
                                    ) : (
                                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-accent flex-shrink-0" />
                                    )}
                                    <span>{text}</span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            ) : null}
        </div>
    );
};

export interface ClassificationResult {
    pattern: string;
    lateralized?: string;
    lateralizationConfidencePct?: number;
    orientationConfidencePct?: number;
    classificationStrategyLabel?: string;
    groundingSide?: 'Left' | 'Right';
    groundingConfidencePct?: number;
    signals?: string[];
    phase1Goal?: string;
    keyMetrics?: string[];
    reasoning?: {
        global: string[];
        lateral: string[];
    };
    reasoningShort?: {
        global: string;
        lateral: string;
        grounding: string;
    };
    picturePosture?: PosturePictureSnapshot;
}

export function selectPosturePhotoTileClassification(
    classification: ClassificationResult | null
): ClassificationResult | null {
    if (!classification) return null;
    const pic = classification.picturePosture;
    if (!pic) return classification;
    return {
        pattern: pic.pattern,
        lateralized: pic.lateralized,
        lateralizationConfidencePct: pic.lateralizationConfidencePct,
        orientationConfidencePct: pic.orientationConfidencePct,
        classificationStrategyLabel: pic.classificationStrategyLabel,
        groundingSide: pic.groundingSide,
        groundingConfidencePct: pic.groundingConfidencePct,
        signals: pic.signals,
        phase1Goal: pic.phase1Goal,
        keyMetrics: pic.keyMetrics,
        reasoning: pic.reasoning,
        reasoningShort: pic.reasoningShort,
    };
}

export function selectPostureIntegratedClassification(
    classification: ClassificationResult | null
): ClassificationResult | null {
    if (!classification) return null;
    const { picturePosture: _p, ...rest } = classification;
    return rest;
}

export interface ClassificationDetailsProps {
    classification: ClassificationResult | null;
    showDetailSections?: boolean;
    laymanMode?: boolean;
}

export const ClassificationDetails: React.FC<ClassificationDetailsProps> = ({
    classification,
    showDetailSections = false,
    laymanMode: laymanModeProp,
}) => {
    const modeStrategy = usePostureMode();
    const laymanMode = laymanModeProp ?? modeStrategy.laymanMode;
    if (!classification) return <p className="text-muted italic font-['Roboto',sans-serif]">Awaiting required views for classification...</p>;

    const footPresentationLine =
        classification.reasoning?.lateral?.find(r => r.toLowerCase().startsWith('foot presentation')) || null;

    const chip = patternChip(classification.pattern);
    const displayPatternLabel = laymanMode
        ? getLaymanPatternLabel(classification.pattern)
        : classification.pattern;
    const displayChipLabel = laymanMode ? getLaymanPatternChipLabel(classification.pattern) : chip.label;
    const displayLateralBadge = laymanMode
        ? getLaymanLateralBadge(classification.lateralized)
        : classification.lateralized;
    const displayPhase1Goal = laymanMode
        ? getLaymanPhase1Goal(classification.phase1Goal)
        : classification.phase1Goal;
    const latPct = clampPct(classification.lateralizationConfidencePct);
    const grdPct = clampPct(classification.groundingConfidencePct);
    const ui = laymanMode ? LAYMAN_CLASSIFICATION_UI : null;

    return (
        <div className="space-y-4 pt-1 font-['Roboto',sans-serif]">
            <div className="rounded-xl border border-line bg-surface p-5 shadow-sm">
                <div className="flex flex-col gap-4">
                    <div className="space-y-3">
                        <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className={`border text-[10px] font-bold uppercase tracking-wider ${chip.className}`}>
                                {displayChipLabel}
                            </Badge>
                            {displayLateralBadge && classification.lateralized !== 'Balanced' ? (
                                <Badge variant="outline" className="border border-line bg-bg text-muted text-[10px] font-bold uppercase tracking-wider">
                                    {displayLateralBadge}
                                </Badge>
                            ) : null}
                        </div>
                        <div className="text-2xl font-bold tracking-tight text-fg font-['Poppins',sans-serif]">
                            {displayPatternLabel}
                        </div>
                        {displayPhase1Goal ? (
                            <div className="rounded-lg bg-accent/10 border border-accent/20 px-3 py-2.5 text-sm">
                                <span className="font-bold text-accent">{ui?.phase1GoalPrefix ?? 'Phase 1 Goal:'}</span>{' '}
                                <span className="text-muted">{displayPhase1Goal}</span>
                            </div>
                        ) : null}
                    </div>

                    <div className="w-full space-y-4">
                        <ConfidenceBar
                            value={clampPct(classification.orientationConfidencePct)}
                            label={ui?.orientationConfidence ?? 'Orientation confidence'}
                            subLabel={
                                laymanMode
                                    ? getLaymanStrategyLabel(classification.classificationStrategyLabel)
                                    : classification.classificationStrategyLabel || 'Current algorithm'
                            }
                            showConfidence={!laymanMode}
                        />
                        {(!laymanMode || latPct > 0) && (
                            <ConfidenceBar
                                value={latPct}
                                label={ui?.lateralizationConfidence ?? 'Lateralization Confidence'}
                                subLabel={
                                    laymanMode
                                        ? (displayLateralBadge ?? 'N/A')
                                        : classification.lateralized && classification.lateralized !== 'Balanced'
                                          ? classification.lateralized
                                          : 'N/A'
                                }
                                showConfidence={!laymanMode}
                            />
                        )}
                        {(!laymanMode || grdPct > 0) && (
                            <ConfidenceBar
                                value={grdPct}
                                label={ui?.groundingConfidence ?? 'Grounding Focus Confidence'}
                                subLabel={
                                    laymanMode
                                        ? (getLaymanGroundingSide(classification.groundingSide) ?? 'N/A')
                                        : classification.groundingSide
                                          ? classification.groundingSide
                                          : 'N/A'
                                }
                                showConfidence={!laymanMode}
                            />
                        )}
                        {footPresentationLine ? (
                            <div className="rounded-lg border border-line bg-bg/50 p-3 text-sm">
                                <div className="text-[10px] font-bold text-muted uppercase tracking-wider mb-1">
                                    {ui?.footPresentationHeading ?? 'Foot Presentation (Posterior)'}
                                </div>
                                <div className="text-muted">
                                    {laymanMode
                                        ? getLaymanReasoningLine(footPresentationLine)
                                        : footPresentationLine.replace(/^Foot Presentation \(Posterior\):\s*/i, '')}
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>

                {classification.keyMetrics && classification.keyMetrics.length > 0 ? (
                    <div className="mt-5 pt-4 border-t border-line">
                        <div className="text-[10px] font-bold text-muted uppercase tracking-wider mb-2">
                            {ui?.keyMetricsHeading ?? 'Key Metrics to Clear Phase 1'}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {classification.keyMetrics.map((metric, i) => (
                                <Badge
                                    key={i}
                                    variant="outline"
                                    className="text-xs py-1 font-bold border-line bg-bg text-muted"
                                >
                                    {laymanMode ? getLaymanKeyMetric(metric) : metric}
                                </Badge>
                            ))}
                        </div>
                    </div>
                ) : null}
            </div>

            {showDetailSections ? (
                <Accordion type="multiple" className="rounded-xl border border-line bg-surface px-4">
                    <AccordionItem value="evidence" className="border-b border-line">
                        <AccordionTrigger className="hover:no-underline py-4">
                            <div className="flex items-center justify-between w-full">
                                <div className="text-sm font-bold text-fg">{ui?.evidenceHeading ?? 'Why this classification?'}</div>
                                {!laymanMode && <div className="text-xs text-muted">Evidence + rules used</div>}
                            </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-4">
                            <div className="grid grid-cols-1 gap-4">
                                <div className="rounded-lg border border-line bg-bg/50 p-3">
                                    <div className="text-[10px] font-bold text-muted uppercase tracking-wider mb-2">
                                        {ui?.globalEvidenceTitle ?? 'Global Pattern Evidence'}
                                    </div>
                                    <GlobalPatternEvidenceList
                                        lines={classification.reasoning?.global ?? []}
                                        laymanMode={laymanMode}
                                    />
                                </div>
                                <div className="rounded-lg border border-line bg-bg/50 p-3">
                                    <div className="text-[10px] font-bold text-muted uppercase tracking-wider mb-2">
                                        {ui?.lateralEvidenceTitle ?? 'Lateralization / Grounding Evidence'}
                                    </div>
                                    <ul className="space-y-1.5">
                                        {(classification.reasoning?.lateral ?? []).map((r, i) => (
                                            <li key={i} className="text-sm flex items-start gap-2 text-muted">
                                                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-accent flex-shrink-0" />
                                                <span>{laymanMode ? getLaymanReasoningLine(r) : r}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="mt-3 text-xs text-muted">
                                        {ui?.evidenceTip ??
                                            'Tip: posture photos provide the best estimate; confidence increases once the passive test battery is completed.'}
                                    </div>
                                </div>
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="signals" className="border-b border-line">
                        <AccordionTrigger className="hover:no-underline py-4">
                            <div className="flex items-center justify-between w-full">
                                <div className="text-sm font-bold text-fg">{ui?.signalsHeading ?? 'Signals (quick tags)'}</div>
                                {!laymanMode && <div className="text-xs text-muted">Short list used in exports</div>}
                            </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-4">
                            <div className="flex flex-wrap gap-2">
                                {(classification.signals ?? []).map((s, i) => (
                                    <Badge key={i} variant="outline" className="bg-bg border-line text-muted text-xs">
                                        {laymanMode ? getLaymanSignal(s) : s}
                                    </Badge>
                                ))}
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="what-next" className="border-none">
                        <AccordionTrigger className="hover:no-underline py-4">
                            <div className="flex items-center justify-between w-full">
                                <div className="text-sm font-bold text-fg">{ui?.nextStepsHeading ?? 'What to do next'}</div>
                                {!laymanMode && <div className="text-xs text-muted">Fast workflow</div>}
                            </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-2">
                            <div className="space-y-3">
                                <div className="rounded-lg border border-line bg-bg/50 p-4">
                                    <div className="text-sm font-bold text-fg">
                                        {ui?.nextStepConfirmTitle ?? 'Confirm side with passive tests'}
                                    </div>
                                    <div className="text-sm text-muted mt-2 leading-relaxed">
                                        {ui?.nextStepConfirmBody ??
                                            'Finish the passive test battery to lock in lateralization/grounding with high certainty.'}
                                    </div>
                                </div>
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            ) : null}
        </div>
    );
};
